// Periodos de los reportes y comparación contra el periodo anterior del mismo largo.
import { useState } from 'react';
import { useConfigNegocio } from '../../hooks/useConfigNegocio';

export type Periodo = 'hoy' | 'semana' | 'mes' | 'personalizado';
export const PERIODOS: [Periodo, string][] = [['hoy', 'Hoy'], ['semana', 'Semana'], ['mes', 'Mes'], ['personalizado', 'Personalizado']];

const DIA_MS = 86_400_000;
export const iso = (d: Date) => d.toLocaleDateString('en-CA');
const fecha = (s: string) => new Date(`${s}T12:00:00`);

// inicioSemana: 0 = domingo … 6 = sábado (clave `inicio_semana` de la configuración; lunes por defecto).
export function rango(p: Periodo, desde: string, hasta: string, hoy = new Date(), inicioSemana = 1): [string, string] {
  if (p === 'hoy') return [iso(hoy), iso(hoy)];
  if (p === 'semana') {
    const transcurridos = (hoy.getDay() - inicioSemana + 7) % 7;
    return [iso(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - transcurridos)), iso(hoy)];
  }
  if (p === 'mes') return [iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), iso(hoy)];
  return desde <= hasta ? [desde, hasta] : [hasta, desde];
}

// Semana: los mismos días de la semana pasada (lun–jue vs lun–jue), no los días justo antes.
// Lo demás: el mismo número de días, justo antes.
export function anterior([ini, fin]: [string, string], p?: Periodo): [string, string] {
  if (p === 'semana') return [iso(new Date(fecha(ini).getTime() - 7 * DIA_MS)), iso(new Date(fecha(fin).getTime() - 7 * DIA_MS))];
  // Mes: del 1 al mismo día del mes pasado (31 de marzo → 1–28 de febrero).
  if (p === 'mes') {
    const f = fecha(fin);
    const ultimo = new Date(f.getFullYear(), f.getMonth(), 0).getDate();
    return [iso(new Date(f.getFullYear(), f.getMonth() - 1, 1)), iso(new Date(f.getFullYear(), f.getMonth() - 1, Math.min(f.getDate(), ultimo)))];
  }
  const dias = Math.round((fecha(fin).getTime() - fecha(ini).getTime()) / DIA_MS) + 1;
  return [iso(new Date(fecha(ini).getTime() - dias * DIA_MS)), iso(new Date(fecha(ini).getTime() - DIA_MS))];
}

const NOMBRE: Record<Periodo, string> = { hoy: 'ayer', semana: 'semana pasada', mes: 'mes pasado', personalizado: 'periodo anterior' };

// "+12% vs semana pasada". Sin base (antes fue 0) no se inventa un porcentaje.
export function delta(actual: number, previo: number, p: Periodo, invertido = false) {
  if (!previo) return { texto: actual ? 'Sin datos del periodo anterior' : '—', color: 'var(--muted)' };
  const pct = Math.round((actual - previo) / Math.abs(previo) * 100);
  const bueno = invertido ? pct < 0 : pct > 0;
  return {
    texto: `${pct > 0 ? '+' : pct < 0 ? '−' : ''}${Math.abs(pct)}% vs ${NOMBRE[p]}`,
    color: pct === 0 ? 'var(--muted)' : bueno ? 'var(--ok)' : 'var(--danger)',
  };
}

export function usePeriodo(inicial: Periodo = 'semana') {
  const [periodo, setPeriodo] = useState<Periodo>(inicial);
  const hoy = iso(new Date());
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(hoy);
  const inicio = parseInt(useConfigNegocio().inicio_semana ?? '1', 10);
  const actual = rango(periodo, desde, hasta, new Date(), inicio >= 0 && inicio <= 6 ? inicio : 1);
  return { periodo, setPeriodo, desde, setDesde, hasta, setHasta, actual, previo: anterior(actual, periodo) };
}
